import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";

const ResultsSection = () => {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total_records: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [testId, setTestId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Scoring");
  const [subTab, setSubTab] = useState("Reading");
  const [writingScore, setWritingScore] = useState("");
  const [feedback, setFeedback] = useState("");

  // ============================================================
  // ⭐ CV AUTO DISPLAY COMPONENT
  // ============================================================
  const CVAutoDisplay = ({ cv }) => {
    if (!cv || typeof cv !== "object")
      return <div className="text-gray-500">Không có dữ liệu CV.</div>;

    return (
      <div className="space-y-4">
        {Object.entries(cv).map(([key, value]) => (
          <div key={key} className="bg-white p-4 border rounded shadow-sm">
            <div className="font-semibold text-gray-700 mb-1">{key}</div>
            <div className="text-gray-800 whitespace-pre-wrap">
              {value ?? ""}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================
  // FETCH API
  // ============================================================
  const fetchData = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        page_size: 20,
      });

      if (testId) params.append("test_id", testId);
      if (status) params.append("status", status);
      if (search) params.append("search", search);

      const url = `/test_attempts/review/all-tests?${params.toString()}`;
      const res = await axios.get(url);

      const sorted = (Array.isArray(res.data.data) ? res.data.data : []).sort(
        (a, b) => b.attempt_id - a.attempt_id
      );

      setData(sorted);
      setMeta({
        page: res.data.page,
        total_pages: res.data.total_pages,
        total_records: res.data.total_records,
      });

    } catch (error) {
      console.error("❌ Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    fetchData(page);
  }, [page, testId, status, search]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= meta.total_pages) {
      setPage(newPage);
    }
  };

  // ============================================================
  // VIEW BÀI LÀM (LOAD CV NẾU CÓ)
  // ============================================================
  const handleView = async (row) => {
    setSelectedAttempt(row);
    setShowModal(true);
    setActiveTab("Scoring");
    setSubTab("Reading");

    if (row.writing_type === "cv" && row.cv_submission_id) {
      try {
        const cv = await axios.get(`/cv_form/${row.cv_submission_id}`);
        setSelectedAttempt((prev) => ({
          ...prev,
          cv_json: cv.data.cv_json,
          cv_text: cv.data.cv_text,
        }));
      } catch (err) {
        console.error("❌ Lỗi tải CV:", err);
      }
    }
  };

  // ============================================================
  // SAVE SCORE
  // ============================================================
  const handleSaveScore = async () => {
    if (!selectedAttempt) {
      alert("Không có bài làm được chọn.");
      return;
    }

    const numericScore = parseFloat(writingScore);
    if (isNaN(numericScore)) {
      alert("Vui lòng nhập điểm hợp lệ.");
      return;
    }
    if (numericScore < 0 || numericScore > 10) {
      alert("Điểm Writing phải nằm trong khoảng 0–10.");
      return;
    }

    const payload = {
      attempt_id: selectedAttempt.attempt_id,
      writing_score: numericScore,
      feedback: feedback || "",
    };

    try {
      await axios.post("/test_attempts/grade", payload);
      alert("✅ Điểm và nhận xét đã được lưu!");
      setShowModal(false);
      setSelectedAttempt(null);
      fetchData(page);
    } catch (err) {
      console.error("❌ Lỗi khi lưu điểm:", err);
      alert(err.response?.data?.detail || "Không thể lưu điểm.");
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white shadow rounded-2xl p-6 border border-gray-100">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          📊 Kết quả tất cả bài test
        </h2>

        {/* Bộ lọc */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Tìm tên học sinh..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={testId}
            onChange={(e) => setTestId(e.target.value)}
          >
            <option value="">Tất cả Test</option>
            <option value="1">Test 1</option>
            <option value="2">Test 2</option>
          </select>

          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Tất cả trạng thái</option>
            <option value="completed">Hoàn thành</option>
            <option value="submitted">Đã nộp</option>
          </select>
        </div>

        {/* Bảng */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-collapse">
            <thead className="bg-blue-50 text-gray-700 font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">Tên học sinh</th>
                <th className="px-4 py-2 text-center">Test ID</th>
                <th className="px-4 py-2 text-center">Scenario</th>
                <th className="px-4 py-2 text-center">Tổng điểm</th>
                <th className="px-4 py-2 text-center">Reading</th>
                <th className="px-4 py-2 text-center">Writing</th>
                <th className="px-4 py-2 text-center">Trạng thái</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {data.length ? (
                data.map((r) => (
                  <tr
                    key={r.attempt_id}
                    onClick={() => handleView(r)}
                    className="cursor-pointer hover:bg-blue-50 transition"
                  >
                    <td className="px-4 py-2">{r.student_name}</td>
                    <td className="px-4 py-2 text-center">{r.test_id}</td>
                    <td className="px-4 py-2 text-center">{r.scenario_id}</td>
                    <td className="px-4 py-2 text-center text-blue-600 font-semibold">
                      {r.total_score}
                    </td>
                    <td className="px-4 py-2 text-center">{r.reading_score}</td>
                    <td className="px-4 py-2 text-center">{r.writing_score}</td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${r.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-4 text-center text-gray-500">
                    Không có dữ liệu phù hợp
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && selectedAttempt && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg w-[900px] max-h-[90vh] overflow-auto relative">

              {/* CLOSE BUTTON */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-50"
              >
                ✖
              </button>

              {/* ⭐ STICKY HEADER ĐƯA RA NGOÀI .p-6 */}
              <div className="sticky top-0 bg-white z-50 px-6 pt-4 pb-3 shadow-sm">

                <h2 className="text-lg font-semibold mb-4">
                  🧾 Chi tiết bài làm #{selectedAttempt.attempt_id}
                </h2>

                {/* Main Tab */}
                <div className="border-b border-gray-200 mb-4 flex space-x-6">
                  <button
                    onClick={() => setActiveTab("Scoring")}
                    className={`pb-2 ${activeTab === "Scoring"
                        ? "border-b-2 border-blue-600 text-blue-600 font-semibold"
                        : "text-gray-500"
                      }`}
                  >
                    Scoring
                  </button>
                </div>

                {/* Sub-tabs */}
                {activeTab === "Scoring" && (
                  <div className="flex gap-4 border-b mb-2">
                    {["Reading", "Writing"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setSubTab(tab)}
                        className={`pb-2 ${subTab === tab
                            ? "border-b-2 border-blue-600 text-blue-600 font-semibold"
                            : "text-gray-500"
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* ⭐ END STICKY HEADER */}

              {/* ⭐ NỘI DUNG – GIỮ NGUYÊN, CHỈ ĐƯA VÀO TRONG p-6 */}
              <div className="p-6">

                {activeTab === "Scoring" && (
                  <>
                    {subTab === "Reading" && (
                      <div>
                        <h3 className="font-semibold text-gray-700 mb-2">
                          📖 Bài đọc của học sinh
                        </h3>
                        <div className="p-4 bg-gray-50 rounded-md text-sm text-gray-700 whitespace-pre-line">
                          {selectedAttempt.student_answer_reading ||
                            "Không có dữ liệu bài đọc."}
                        </div>
                      </div>
                    )}

                    {subTab === "Writing" && (
                      <div>
                        <h3 className="font-semibold text-gray-700 mb-2">
                          ✍️ Bài viết / CV của học sinh
                        </h3>

                        {selectedAttempt.writing_type === "cv" ? (
                          <div className="p-4 bg-gray-50 rounded-md">
                            <h4 className="font-semibold mb-3">📄 CV Preview</h4>

                            <CVAutoDisplay cv={selectedAttempt.cv_json} />

                            <h4 className="font-semibold mt-6 mb-2">📃 CV Text</h4>
                            <div className="p-3 bg-white border rounded whitespace-pre-wrap">
                              {selectedAttempt.cv_text || "Không có CV Text."}
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-gray-50 rounded-md text-sm text-gray-700 whitespace-pre-line">
                            {selectedAttempt?.writing_content?.trim()
                              ? selectedAttempt.writing_content
                              : "Không có dữ liệu bài viết."}
                          </div>
                        )}

                        {/* FORM CHẤM ĐIỂM */}
                        <div className="space-y-3 mt-4">
                          <label className="block text-sm font-semibold text-gray-700">
                            Điểm Writing:
                          </label>
                          <input
                            type="number"
                            value={writingScore}
                            min={0}
                            max={10}
                            step={0.5}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") return setWritingScore("");
                              let num = parseFloat(val);
                              if (isNaN(num)) num = 0;
                              if (num < 0) num = 0;
                              if (num > 10) num = 10;
                              setWritingScore(num);
                            }}
                            className="border p-2 rounded"
                            placeholder="Nhập điểm (0–10)"
                          />

                          <label className="block text-sm font-semibold text-gray-700">
                            Nhận xét của giáo viên:
                          </label>
                          <textarea
                            rows="4"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            className="border rounded-lg p-2 w-full"
                            placeholder="Nhập nhận xét..."
                          ></textarea>

                          <button
                            onClick={handleSaveScore}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                          >
                            💾 Lưu điểm
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ResultsSection;
